import posthog, {CaptureResult, CapturedNetworkRequest} from 'posthog-js';

const API_KEY = import.meta.env.VITE_POSTHOG_API_KEY || '';
const HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

const URL_PATTERN = /https?:\/\/[^\s"'<>]+/g;
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(\.[\w-]+)*\.[a-z]{2,}/gi;
const RESET_PASSWORD_PATTERN = /\/resetPassword\/[^/?#\s"'<>]+/g;

function scrubText(text: string): string {
    return text
        .replace(URL_PATTERN, (url) => url.split(/[?#]/)[0])
        .replace(RESET_PASSWORD_PATTERN, '/resetPassword/:code')
        .replace(EMAIL_PATTERN, '[email]');
}

function scrub(value: unknown): unknown {
    if (typeof value === 'string') return scrubText(value);
    if (Array.isArray(value)) return value.map(scrub);
    if (value && Object.getPrototypeOf(value) === Object.prototype) {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrub(item)]));
    }
    return value;
}

function scrubEvent(event: CaptureResult | null): CaptureResult | null {
    if (!event || event.event === '$snapshot') return event;
    return scrub(event) as CaptureResult;
}

function scrubRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
    return {...request, name: scrubText(request.name)};
}

const POSTHOG_OPTIONS = {
    api_host: HOST,
    defaults: '2026-05-30' as const,
    person_profiles: 'identified_only' as const,
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
    },
    mask_all_text: true,
    mask_all_element_attributes: true,
    before_send: scrubEvent,
    session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskCapturedNetworkRequestFn: scrubRequest,
        sampleRate: 0.2,
    },
};

export function isPostHogEnabled(): boolean {
    return Boolean(API_KEY);
}

export function initPostHog(app: string): void {
    if (!API_KEY) {
        return;
    }

    posthog.init(API_KEY, {
        ...POSTHOG_OPTIONS,
        loaded: (client) => {
            client.register({
                app,
                environment: import.meta.env.MODE,
            });
        },
    });
}

export function identifyPostHogUser(user?: {_id?: string} | null): void {
    if (!API_KEY) {
        return;
    }

    if (!user?._id) {
        posthog.reset();
        return;
    }

    posthog.identify(user._id);
}

export function capturePostHogEvent(event: string, properties?: Record<string, unknown>): void {
    if (!API_KEY) {
        return;
    }

    posthog.capture(event, properties);
}

export {posthog};
